import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationService } from '../../api/services';
import {
  LayoutDashboard, Calendar, TrendingUp, Bell, Settings,
  MapPin, LogOut, Menu, X, Video, BookOpen, ShieldCheck,
  Users, ChevronRight, PenLine, BarChart2, ClipboardList, MessageCircle, FolderOpen, FileText, Send, 
  Shield, Play} from 'lucide-react';

const clientNav = [
  { label: 'Dashboard',      to: '/dashboard',      icon: LayoutDashboard },
  { label: 'My Cohort',      to: '/my-cohort',      icon: FolderOpen },
  { label: 'Bookings',       to: '/bookings',        icon: Calendar },
  { label: 'My Sessions',    to: '/my-sessions',     icon: Video },
  { label: 'Group Sessions', to: '/group-sessions',   icon: Users },
  { label: 'Communities',    to: '/communities',       icon: MessageCircle },
  { label: 'My Progress',    to: '/progress',        icon: TrendingUp },
  { label: 'Documents',      to: '/documents',       icon: FolderOpen },
  { label: 'Timesheet',      to: '/timesheet',       icon: FileText },
  { label: 'Notifications',  to: '/notifications',   icon: Bell },
  { label: 'Settings',       to: '/settings',        icon: Settings },
  { label: 'Help',           to: '/help',            icon: BookOpen },
];

const expertNav = [
  { label: 'Dashboard',      to: '/expert/dashboard',   icon: LayoutDashboard },
  { label: 'My Cohorts',     to: '/expert/cohorts',     icon: FolderOpen },
  { label: 'Bookings',       to: '/expert/bookings',    icon: Calendar },
  { label: 'My Sessions',    to: '/expert/sessions',    icon: Video },
  { label: 'Group Sessions', to: '/group-sessions',      icon: Users },
  { label: 'Communities',    to: '/communities',          icon: MessageCircle },
  { label: 'Submissions',    to: '/expert/submissions', icon: PenLine },
  { label: 'Timesheet',      to: '/expert/timesheet',   icon: FileText },
  { label: 'Notifications',  to: '/notifications',      icon: Bell },
  { label: 'Settings',       to: '/settings',           icon: Settings },
  { label: 'Help',           to: '/help',            icon: BookOpen },
];

const adminNav = [
  { label: 'Dashboard',       to: '/admin',          icon: LayoutDashboard },
  { label: 'Expert Approval', to: '/admin/experts',  icon: ShieldCheck },
  { label: 'Clients',         to: '/admin/clients',  icon: Users },
  { label: 'Sessions',        to: '/admin/sessions', icon: Video },
  { label: 'Bookings',        to: '/admin/bookings', icon: Calendar },
  { label: 'Group Sessions',  to: '/admin/group-sessions', icon: Users },
  { label: 'Levels',          to: '/admin/levels',   icon: BookOpen },
  { label: 'Documents',       to: '/admin/documents',icon: FolderOpen },
  { label: 'Audit Log',       to: '/admin/audit-log',icon: Shield },
  { label: 'Broadcast',       to: '/admin/broadcast', icon: Send },
  { label: 'Reports & Payments', to: '/admin/reports', icon: BarChart2 },
  { label: 'Notifications',   to: '/notifications',  icon: Bell },
  { label: 'Assessments',      to: '/admin/assessments', icon: ClipboardList },
  { label: 'Seminars',          to: '/admin/seminars',             icon: Video },
  { label: 'Cohorts',           to: '/admin/cohorts',              icon: FolderOpen },
  { label: 'Completed Projects', to: '/admin/completed-projects',  icon: Play },
  { label: 'Platform Settings', to: '/admin/settings',             icon: Settings },
  { label: 'Help',              to: '/admin/help',        icon: BookOpen },
];

const NavItem = ({ item, active }) => (
  <Link
    to={item.to}
    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all group ${
      active
        ? 'bg-forest-800 text-cream-50'
        : 'text-forest-600 hover:bg-cream-100 hover:text-forest-900'
    }`}
  >
    <item.icon size={17} className={active ? 'text-forest-300' : 'text-forest-400 group-hover:text-forest-600'} />
    {item.label}
    {active && <ChevronRight size={13} className="ml-auto text-forest-400" />}
  </Link>
);

const DashboardLayout = ({ children, title }) => {
  const { user, logout, isAdmin, isExpert } = useAuth();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Poll unread count every 30 seconds — NOT on every render
  useEffect(() => {
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const res = await notificationService.getUnreadCount();
        const count = res.data?.data?.unreadCount ?? res.data?.unreadCount ?? 0;
        if (!cancelled) setUnread(count);
      } catch {
        // silently fail — don't crash layout for a badge count
      }
    };

    fetchCount(); // fetch immediately on mount
    const interval = setInterval(fetchCount, 30000); // then every 30s

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []); // only runs once on mount

  const nav        = isAdmin ? adminNav : isExpert ? expertNav : clientNav;
  const initials   = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;
  const fullName   = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  const roleLabel  = isAdmin ? 'Super Admin' : isExpert ? 'Expert' : 'Client';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (to) => {
    if (location.pathname === to) return true;
    // Don't match root dashboard paths as prefix for sub-routes
    const exactPaths = ['/dashboard', '/admin', '/expert/dashboard'];
    if (exactPaths.includes(to)) return false;
    return location.pathname.startsWith(to + '/') || location.pathname.startsWith(to);
  };

  const SidebarContent = ({ onClose }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-4 py-5 border-b border-cream-100">
        <Link to="/" onClick={onClose} className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-forest-800 rounded-lg flex items-center justify-center">
            <MapPin size={15} className="text-forest-300" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif font-bold text-forest-900 text-base">
              TourEnt <span className="text-rust-500">Hub</span>
            </span>
            <span className="text-[8px] font-sans uppercase tracking-widest text-forest-400">
              {roleLabel} Portal
            </span>
          </div>
        </Link>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {nav.map((item) => (
          <NavItem key={item.to} item={item} active={isActive(item.to)} />
        ))}
      </nav>

      {/* User card */}
      <div className="px-3 py-4 border-t border-cream-100">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-cream-50 border border-cream-100">
          <div className="w-8 h-8 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 font-serif font-bold text-sm flex-shrink-0 overflow-hidden">
            {user?.avatar
              ? <img src={user.avatar} alt={fullName} className="w-full h-full object-cover" />
              : initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-forest-900 truncate">{fullName}</div>
            <div className="text-[10px] text-forest-400">{roleLabel}</div>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 text-forest-300 hover:text-rust-500 rounded-lg hover:bg-rust-50 transition-colors flex-shrink-0"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 xl:w-64 bg-white border-r border-cream-100 fixed inset-y-0 left-0 z-30">
        <SidebarContent onClose={() => {}} />
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-forest-900/40" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-64 bg-white flex flex-col h-full shadow-2xl">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 text-forest-400 hover:text-forest-700 z-10"
            >
              <X size={20} />
            </button>
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-60 xl:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="bg-white border-b border-cream-100 px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-forest-600 hover:text-forest-900 rounded-lg hover:bg-cream-50 transition-colors"
            >
              <Menu size={20} />
            </button>
            {title && <h1 className="font-serif text-xl text-forest-900">{title}</h1>}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/notifications"
              className="relative p-2 text-forest-500 hover:text-forest-900 rounded-lg hover:bg-cream-50 transition-colors"
            >
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-rust-500 rounded-full text-white text-[9px] flex items-center justify-center font-medium">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </Link>
            <Link
              to="/settings"
              className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-cream-50 transition-colors"
            >
              <div className="w-7 h-7 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 font-serif font-bold text-xs overflow-hidden">
                {user?.avatar
                  ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  : initials}
              </div>
              <span className="hidden sm:block text-sm font-medium text-forest-700">{user?.firstName}</span>
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 xl:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;